import { useEffect } from 'react';
import { useBlogState } from './useBlogState';
import { useBlogQueries } from './useBlogQueries';
import { useBlogFilters } from './useBlogFilters';
import { useBlogActions } from './useBlogActions';
import { UseBlogContextValue } from './types';
import { readFile } from '../../utils/imageUtils';

export const useBlogContext = (): UseBlogContextValue => {
  const state = useBlogState();
  const queries = useBlogQueries(state.setNotes);
  const { filteredNotes, totalPages, displayedNotes } = useBlogFilters(
    state.notes, state.search, state.tagFilter, queries.isAuthenticated, queries.meData?.me?.id,
    state.paginationMode, state.currentPage, state.loadedCount
  );

  // Уведомления: авто-скрытие
  useEffect(() => {
    if (state.notification) {
      const timer = setTimeout(() => state.setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [state, state.notification]);

  // Действия
  const actions = useBlogActions(state, state, queries, state.setNotification);

  const handleCropComplete = async (croppedImageBase64: string) => {
    // Мы просто сохраняем то, что дала библиотека
    state.setForm(prev => ({
      ...prev,
      previewImage: croppedImageBase64,
      image: croppedImageBase64,
    }));
  };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const validFiles = Array.from(e.target.files).filter(f => f.size <= 10 * 1024 * 1024);
    if (validFiles.length < e.target.files.length) {
      state.setNotification({ type: 'error', message: 'Некоторые файлы превышают 10 МБ и были пропущены' });
    }
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = (reader.result as string).split(',')[1];
        const contentType = file.type || (file.name.toLowerCase().endsWith('.msi') ? 'application/x-msdownload' : 'application/octet-stream');
        state.setForm((prev: any) => ({
          ...prev, attachments: [...prev.attachments, { filename: file.name, contentType, data: base64Data }]
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      state.setNotification({ type: 'error', message: 'Файл слишком большой (более 10 МБ)' });
      return;
    }

    if (!file.type.match(/image\/(jpeg|png|gif|webp)/)) {
      state.setNotification({ type: 'error', message: 'Недопустимый формат изображения' });
      return;
    }

    state.setImageState(prev => ({ ...prev, loading: true }));

    try {
      const imageData = await readFile(file);
      state.setImageState({ original: imageData, cropOpen: true, loading: false });
    } catch (error) {
      state.setNotification({ type: 'error', message: 'Ошибка загрузки изображения' });
      state.setImageState(prev => ({ ...prev, loading: false }));
    }
  };

  const removeAttachment = (index: number) => {
    state.setForm((prev: any) => ({ ...prev, attachments: prev.attachments.filter((_: any, i: number) => i !== index) }));
  };

  return {
    notes: displayedNotes,
    loading: queries.loading,
    currentNote: state.currentNote,
    form: state.form,
    imageState: state.imageState,
    mode: state.mode,
    isCreating: state.isCreating,
    selectedNotes: state.selectedNotes,
    search: state.search,
    tagFilter: state.tagFilter,
    confirmDeleteId: state.confirmDeleteId,
    confirmDeleteMultiple: state.confirmDeleteMultiple,
    paginationMode: state.paginationMode,
    currentPage: state.currentPage,
    totalPages,
    totalNotesCount: filteredNotes.length,
    loadedCount: state.loadedCount,
    notification: state.notification,
    isAuthenticated: queries.isAuthenticated,
    isAdmin: queries.isAdmin,
    user: queries.meData?.me || null,

    setIsCreating: state.setIsCreating,
    setMode: state.setMode,
    setCurrentNote: state.setCurrentNote,
    setSelectedNotes: state.setSelectedNotes,
    setSearch: state.setSearch,
    setTagFilter: state.setTagFilter,
    setForm: (partial: any) => state.setForm((prev: any) => ({ ...prev, ...partial })),
    setImageState: (partial: any) => state.setImageState((prev: any) => ({ ...prev, ...partial })),
    setConfirmDeleteId: state.setConfirmDeleteId,
    setConfirmDeleteMultiple: state.setConfirmDeleteMultiple,
    setPaginationMode: state.setPaginationMode,
    setNotification: state.setNotification,

    handleCreate: actions.handleCreate,
    handleEdit: actions.handleEdit,
    handleDeleteNote: actions.handleDeleteNote,
    handleDeleteSelected: actions.handleDeleteSelected,
    handleToggleFavorite: actions.handleToggleFavorite,
    confirmAndDeleteNote: actions.confirmAndDeleteNote,
    confirmAndDeleteSelected: actions.confirmAndDeleteSelected,
    handleImageUpload,
    handleCropComplete,
    handleFileChange,
    removeAttachment,
    handleShowMore: () => state.setLoadedCount(prev => prev + 8),
    handlePageClick: (page: number) => {
      if (page >= 1 && page <= totalPages) {
        state.setCurrentPage(page);
        state.setPaginationMode('pages');
      }
    },
    fileInputRef: state.fileInputRef,
    attachmentInputRef: state.attachmentInputRef,
  };
};